/**
 * Centralized Logger Utility
 * 
 * Provides consistent logging across the application with:
 * - Environment-based log levels
 * - Structured logging
 * - Production-safe logging
 * - Future-ready for error tracking integration
 */

const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
};

class Logger {
  constructor() {
    // Set log level based on environment
    this.level = process.env.NODE_ENV === 'production' 
      ? LOG_LEVELS.ERROR  // Only errors in production
      : LOG_LEVELS.DEBUG; // All logs in development
    
    // Store log history for debugging (optional, limit size)
    this.logHistory = [];
    this.maxHistorySize = 100;
  }

  /**
   * Set the log level programmatically
   * @param {number} level - LOG_LEVELS constant
   */
  setLevel(level) {
    this.level = level;
  }

  /**
   * Get current log level
   * @returns {number} Current log level
   */
  getLevel() {
    return this.level;
  }

  /**
   * Add log entry to history
   * @private
   */
  _addToHistory(level, message, ...args) {
    this.logHistory.push({
      timestamp: new Date().toISOString(),
      level,
      message,
      args: args.length > 0 ? args : undefined
    });

    // Limit history size
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }
  }

  /**
   * Debug level logging (development only)
   * @param {string} message - Log message
   * @param {...any} args - Additional arguments
   */
  debug(message, ...args) {
    if (this.level <= LOG_LEVELS.DEBUG) {
      console.log(`[DEBUG] ${message}`, ...args);
      this._addToHistory('DEBUG', message, ...args);
    }
  }

  /**
   * Info level logging
   * @param {string} message - Log message
   * @param {...any} args - Additional arguments
   */
  info(message, ...args) {
    if (this.level <= LOG_LEVELS.INFO) {
      console.info(`[INFO] ${message}`, ...args);
      this._addToHistory('INFO', message, ...args);
    }
  }

  /**
   * Warning level logging
   * @param {string} message - Log message
   * @param {...any} args - Additional arguments
   */
  warn(message, ...args) {
    if (this.level <= LOG_LEVELS.WARN) {
      console.warn(`[WARN] ${message}`, ...args);
      this._addToHistory('WARN', message, ...args);
    }
  }

  /**
   * Error level logging
   * @param {string} message - Log message
   * @param {Error} error - Error object (optional)
   * @param {...any} args - Additional arguments
   */
  error(message, error, ...args) {
    if (this.level <= LOG_LEVELS.ERROR) {
      console.error(`[ERROR] ${message}`, error, ...args);
      this._addToHistory('ERROR', message, error, ...args);

      // In production, send to error tracking service
      if (process.env.NODE_ENV === 'production' && error) {
        // TODO: Integrate with Sentry, LogRocket, or similar
        // Example:
        // if (window.Sentry) {
        //   window.Sentry.captureException(error, {
        //     extra: { message, args }
        //   });
        // }
      }
    }
  }

  /**
   * Group related logs together
   * @param {string} label - Group label
   * @param {Function} callback - Function containing grouped logs
   */
  group(label, callback) {
    if (this.level <= LOG_LEVELS.DEBUG) {
      console.group(label);
      callback();
      console.groupEnd();
    } else {
      callback();
    }
  }

  /**
   * Log a table (useful for objects/arrays)
   * @param {string} label - Table label
   * @param {*} data - Data to display as table
   */
  table(label, data) {
    if (this.level <= LOG_LEVELS.DEBUG) {
      console.group(label);
      console.table(data);
      console.groupEnd();
    }
  }

  /**
   * Get log history (for debugging)
   * @returns {Array} Log history
   */
  getHistory() {
    return [...this.logHistory];
  }

  /**
   * Clear log history
   */
  clearHistory() {
    this.logHistory = [];
  }

  /**
   * Export log history as JSON (for debugging/reporting)
   * @returns {string} JSON string of log history
   */
  exportHistory() {
    return JSON.stringify(this.logHistory, null, 2);
  }
}

// Create singleton instance
const logger = new Logger();

// Export both the instance and the class (for testing)
export default logger;
export { Logger, LOG_LEVELS };

